/**
 * El canal con la app de la clienta (`KabanduApp`, webview_flutter). La app le presta su sesión a
 * la página: antes de CADA llamada al server se pide un ID token fresco (la app lo renueva si
 * venció), así que la página nunca lo guarda. Protocolo y reglas puras en `consent.ts`.
 */
import { leerRespuestaApp } from './consent';

declare global {
    interface Window {
        KabanduApp?: { postMessage: (mensaje: string) => void };
        kabanduAppReceive?: (mensaje: unknown) => void;
    }
}

type Pendiente = { resolve: (token: string) => void; reject: (error: Error) => void; timer: number };

const pendientes = new Map<string, Pendiente>();
let consecutivo = 0;

const instalarReceptor = (): void => {
    if (window.kabanduAppReceive) return;
    window.kabanduAppReceive = (raw: unknown) => {
        const respuesta = leerRespuestaApp(raw);
        if (!respuesta) return;
        const pendiente = pendientes.get(respuesta.requestId);
        if (!pendiente) return;
        pendientes.delete(respuesta.requestId);
        window.clearTimeout(pendiente.timer);
        if (respuesta.ok) pendiente.resolve(respuesta.idToken);
        else pendiente.reject(new Error(respuesta.mensaje));
    };
};

export const tieneCanalDeApp = (): boolean => typeof window !== 'undefined' && !!window.KabanduApp?.postMessage;

/**
 * El canal lo inyecta la app al cargar la página; por si llega un instante después del primer
 * render, se le da un margen corto antes de decidir que no estamos en la app.
 */
export const esperarCanalDeApp = async (ms = 1200): Promise<boolean> => {
    const inicio = Date.now();
    while (!tieneCanalDeApp() && Date.now() - inicio < ms) {
        await new Promise((r) => setTimeout(r, 100));
    }
    return tieneCanalDeApp();
};

export const pedirIdToken = (): Promise<string> => {
    instalarReceptor();
    const canal = window.KabanduApp;
    if (!canal) return Promise.reject(new Error('Abre tu contrato desde la app Kabandú.'));
    consecutivo += 1;
    const requestId = `t${Date.now()}-${consecutivo}`;
    return new Promise<string>((resolve, reject) => {
        const timer = window.setTimeout(() => {
            pendientes.delete(requestId);
            reject(new Error('La app no respondió. Cierra el contrato y vuelve a abrirlo.'));
        }, 15000);
        pendientes.set(requestId, { resolve, reject, timer });
        canal.postMessage(JSON.stringify({ type: 'getIdToken', requestId }));
    });
};

/** La página terminó: la app la cierra y vuelve a leer el expediente (el aviso se quita solo). */
export const avisarTerminado = (): void => {
    window.KabanduApp?.postMessage(JSON.stringify({ type: 'done' }));
};
